import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ErpatApiError } from '@erpat/sdk';
import { surveysService, type SurveyRespondAction, type SurveyAnswerMap } from '@/services/erpat';
import { errorMessage } from '@/services/api/http';
import { getSessionEpoch, hasAccessToken } from '@/services/api/erpat-client';
import type { SurveyPromptVM } from '@/services/api/models';

/**
 * The pending survey/consent prompt queue + the popup's submit state.
 *
 * Lives in a store (not a component) so the queue survives route changes, is
 * reachable from the always-mounted `SurveyPromptHost`, and can be reset from
 * `session.store.signOutLocal()` alongside the messages store.
 *
 * ⚠ Fetch discipline: the backend counts one impression against the queue head
 * per `GET /me/surveys` (it caps how many times an optional survey may ASK), so
 * fetching is throttled and happens only on real triggers — the auth transition,
 * return-to-foreground, and after a respond. Never hook this to token rotations
 * or tab switches, and never call `surveysService.pending()` from a view.
 *
 * The backend owns ALL pacing state (snooze, impressions, opt-out) — nothing is
 * persisted locally. `lastAnsweredToken` is only an in-memory race filter for
 * the window between a respond and the force re-fetch that follows it.
 */
export const useSurveysStore = defineStore('surveys', () => {
  /** Pending prompts, most-urgent first (server order). Always REASSIGNED, never mutated. */
  const queue = ref<SurveyPromptVM[]>([]);
  const fetching = ref(false);
  const submitting = ref(false);
  /** Submit failure shown inline inside the modal (the modal stays open). */
  const respondError = ref<string | null>(null);
  /** 422 `details.fields` keyed by question id — per-question inline errors. */
  const fieldErrors = ref<Record<string, string>>({});
  const lastFetchedAt = ref<number | null>(null);
  /** 404 `module_unavailable` — the feature does not exist for this tenant/session. */
  const unavailable = ref(false);

  /** The survey the popup presents — the queue head, or null (popup closed). */
  const current = computed(() => queue.value[0] ?? null);

  /** Impressions are billed per fetch — hold a fetched queue for at least this long. */
  const FETCH_THROTTLE_MS = 5 * 60 * 1000;

  /**
   * How long a 404 keeps the feature switched off before the next trigger re-probes.
   * Without an expiry, an operator enabling Survey Settings → Mobile prompts
   * mid-session stays invisible until sign-out or a cold start (native WebViews live
   * for days). Re-probing is free: the backend refuses a disabled surface in the
   * controller constructor, before any impression is recorded.
   */
  const UNAVAILABLE_RETRY_MS = 30 * 60 * 1000;

  let controller: AbortController | null = null;
  /** When `unavailable` was last latched (epoch ms). */
  let unavailableAt = 0;
  /** One-shot filter: a just-answered token must not flash back in from a stale fetch. */
  let lastAnsweredToken: string | null = null;

  /**
   * (Re)load the pending queue. Silent on every failure — a survey prompt is an
   * enhancement, and a background fetch must never toast, block, or blank the app.
   */
  async function fetchPending(opts: { force?: boolean } = {}): Promise<void> {
    if (!hasAccessToken()) return;
    if (unavailable.value) {
      if (Date.now() - unavailableAt < UNAVAILABLE_RETRY_MS) return;
      unavailable.value = false;
    }
    if (!opts.force) {
      if (fetching.value) return;
      if (lastFetchedAt.value !== null && Date.now() - lastFetchedAt.value < FETCH_THROTTLE_MS) return;
    }

    controller?.abort();
    controller = new AbortController();
    const { signal } = controller;

    // Capture the session generation before awaiting: a fetch resolving after a
    // sign-out must never pop a survey modal over the sign-in screen.
    const epoch = getSessionEpoch();

    fetching.value = true;
    try {
      const items = await surveysService.pending(signal);
      if (signal.aborted || getSessionEpoch() !== epoch) return;
      lastFetchedAt.value = Date.now();
      queue.value = lastAnsweredToken
        ? items.filter((s) => s.token !== lastAnsweredToken)
        : items;
      lastAnsweredToken = null;
    } catch (err) {
      // Covers store-initiated aborts too: the SDK never lets a DOMException
      // escape — it rethrows aborted fetches as ErpatApiError('network_error').
      if (signal.aborted || getSessionEpoch() !== epoch) return;
      if (err instanceof ErpatApiError && (err.status === 404 || err.status === 403)) {
        // module_unavailable (module/policy off), a missing route on this
        // backend, or forbidden — either way the feature does not exist here.
        // Stay silent until the latch expires (reset() also re-arms it on sign-in).
        markUnavailable();
        return;
      }
      // Any other failure (network, 5xx): keep the existing queue, stay silent.
    } finally {
      if (!signal.aborted) fetching.value = false;
    }
  }

  /**
   * Record what the user did with the CURRENT survey, then advance: the head is
   * dropped immediately (instant advance / close) and a background force re-fetch
   * re-syncs with the server — which also records the next head's impression.
   */
  async function respond(action: SurveyRespondAction, answers?: SurveyAnswerMap): Promise<void> {
    const survey = current.value;
    if (!survey || submitting.value) return;

    const token = survey.token;
    const epoch = getSessionEpoch();
    submitting.value = true;
    respondError.value = null;
    fieldErrors.value = {};

    try {
      await surveysService.respond(token, action, answers);
      if (getSessionEpoch() !== epoch) return;
      advancePast(token);
    } catch (err) {
      if (getSessionEpoch() !== epoch) return;
      if (err instanceof ErpatApiError) {
        if (err.code === 'validation_failed' && err.status === 422) {
          const fields = (err.details?.fields ?? {}) as Record<string, unknown>;
          const mapped: Record<string, string> = {};
          for (const [id, msg] of Object.entries(fields)) mapped[id] = `This field ${String(msg)}.`;
          fieldErrors.value = mapped;
          respondError.value = 'Please review the highlighted questions.';
          return; // modal stays open
        }
        if (err.code === 'survey_not_found') {
          // No longer pending — answered on another device, archived, or the
          // window closed since we fetched. The user's intent is moot; advance
          // silently rather than showing an error they cannot resolve.
          advancePast(token);
          return;
        }
        if (err.status === 404) {
          // module_unavailable (switched off mid-session) or the route missing
          // on this backend — the feature does not exist here. Close silently.
          markUnavailable();
          return;
        }
      }
      // Anything else (409 action_not_allowed, network, 5xx): keep the modal
      // open with an inline message so the user can retry.
      respondError.value = errorMessage(err, 'Unable to record your response. Please try again.');
    } finally {
      if (getSessionEpoch() === epoch) submitting.value = false;
    }
  }

  /** Latch the feature off (see UNAVAILABLE_RETRY_MS) and close any open prompt. */
  function markUnavailable(): void {
    unavailable.value = true;
    unavailableAt = Date.now();
    queue.value = [];
  }

  /** Drop one token from the queue and re-sync with the server in the background. */
  function advancePast(token: string): void {
    lastAnsweredToken = token;
    queue.value = queue.value.filter((s) => s.token !== token);
    respondError.value = null;
    fieldErrors.value = {};
    void fetchPending({ force: true });
  }

  /** Clear all state — call on sign-out so the next user starts clean. */
  function reset(): void {
    controller?.abort();
    controller = null;
    queue.value = [];
    fetching.value = false;
    submitting.value = false;
    respondError.value = null;
    fieldErrors.value = {};
    lastFetchedAt.value = null;
    unavailable.value = false;
    unavailableAt = 0;
    lastAnsweredToken = null;
  }

  return {
    queue,
    fetching,
    submitting,
    respondError,
    fieldErrors,
    lastFetchedAt,
    unavailable,
    current,
    fetchPending,
    respond,
    reset,
  };
});
